<script setup lang="ts">
const { t } = useTexts();
const { userCanOnlyWithdraw } = useUserLimits();
const { openToaster } = useLayoutStore();
const { isRedeemableCashbackEnabled } = useFeatureFlagsJson();

const {
  walletIsLoading,
  updateWallet,
  walletCashbackNumber,
  walletCashbackFormatted,
  walletCashbackExpiryDatetime,
  walletHasCashback,
} = useWallet();

const isCashbackWalletVisible = computed<boolean>(() => {
  return (
    isRedeemableCashbackEnabled.value &&
    walletHasCashback.value &&
    !userCanOnlyWithdraw.value
  );
});

const isModalVisible = ref<boolean>(false);
const isLoading = ref<boolean>(false);

const countDownTotalTime = computed(() => {
  const now = new Date();
  const expireDatetime = new Date(walletCashbackExpiryDatetime.value);

  return expireDatetime.getTime() > now.getTime()
    ? (expireDatetime.getTime() - now.getTime()) / 1000
    : 0;
});

const isCountDownFinished = ref<boolean>(false);

const walletCashbackIsExpired = computed<boolean>(() => {
  if (isCountDownFinished.value) {
    return true;
  }

  return new Date(walletCashbackExpiryDatetime.value) < new Date();
});

function onCashbackExpired() {
  isCountDownFinished.value = true;
  isModalVisible.value = false;
}

function requestBonusWithdraw() {
  if (walletCashbackNumber.value <= 0 || walletCashbackIsExpired.value) {
    openToaster({
      message: t('account.wallet.no_cashback_to_redeem'),
      type: 'error',
    });

    return;
  }

  const { ApiService } = useApiService();

  isLoading.value = true;

  ApiService.post('/cashback/transfer', {})
    .then((res) => {
      openToaster({
        message: res.data.message,
        type: 'success',
      });
      isModalVisible.value = false;
      updateWallet();
    })
    .catch((err) => {
      const message = err?.data?.message || err?.message || t('general.error');

      openToaster({
        message,
        type: 'error',
      });
    })
    .finally(() => {
      isLoading.value = false;
    });
}
</script>

<template>
  <UserBox
    v-if="isCashbackWalletVisible"
    :class="$style.walletContainer"
  >
    <div :class="$style.topContent">
      <div :class="$style.walletIcon">
        <NuxtIcon name="user/cashback" />
      </div>

      <span :class="$style.userCurrencyAndId">
        <DepositCurrencyButton
          :is-bonus-icon="true"
          :user-currency="$t('account.wallet.cashback')"
        />
      </span>

      <strong :class="[$style.walletAmount, { 'opacity-30': walletIsLoading }]">
        {{ walletCashbackFormatted }}
      </strong>

      <div
        v-if="countDownTotalTime && !walletCashbackIsExpired"
        :class="$style.cashbackCountdown"
      >
        <WidgetsCountdown
          :total-time="countDownTotalTime"
          @timer-finished="onCashbackExpired"
        >
          <template #process="{ time }">
            <span :class="$style.countdownTitle">
              {{ $t('account.wallet.cashback_expire_time') }}
            </span>
            <span :class="$style.countdownTime">
              {{ `${time.hours}h : ${time.minutes}m : ${time.seconds}s` }}
            </span>
          </template>
          <template #finish>
            <span :class="$style.countdownTitle">
              {{ $t('account.wallet.cashback_expired') }}
            </span>
          </template>
        </WidgetsCountdown>
      </div>

      <span
        v-else
        :class="$style.isExpired"
      >
        {{ $t('account.wallet.cashback_expired') }}
      </span>
    </div>

    <div :class="$style.walletBoxBtns">
      <button
        :class="[$style.walletBtn, $style.redeemBtn]"
        :disabled="walletCashbackIsExpired"
        @click="isModalVisible = true"
      >
        {{ $t('account.wallet.redeem') }}
      </button>
    </div>
  </UserBox>

  <BaseModal
    v-model="isModalVisible"
    width="500"
  >
    <div :class="$style.modalRedeem">
      <BaseLoading
        class="!bg-auth-bg-primary"
        :show="isLoading"
      />

      <div :class="$style.infoText">
        <span>
          {{ $t('account.wallet.cashback_redeem_amount') }}
        </span>
        <strong>{{ walletCashbackFormatted }}</strong>
      </div>

      <p :class="$style.confirmText">
        <strong>
          {{ $t('account.wallet.cashback_redeem_confirm') }}
        </strong>
        <span>
          {{ $t('account.wallet.cashback_redeem_confirm_message') }}
        </span>
      </p>

      <div :class="$style.confirmationButtons">
        <button
          class="step-actions-btn step-actions-btn-true"
          :disabled="walletCashbackIsExpired"
          @click="requestBonusWithdraw()"
        >
          {{ $t('general.confirm') }}
        </button>

        <button
          class="step-actions-btn step-actions-btn-false"
          @click="isModalVisible = false"
        >
          {{ $t('general.cancel') }}
        </button>
      </div>
    </div>
  </BaseModal>
</template>

<style module lang="postcss">
.walletContainer {
  @apply min-w-[70vw] md:min-w-[365px] leading-[100%] min-h-full flex flex-col;

  .topContent {
    @apply pb-2 relative flex flex-col items-start justify-start;

    .walletIcon {
      @apply absolute text-texts opacity-20 top-0 lg:top-2 right-0 lg:right-2  w-8 lg:w-12 h-8 lg:h-12;

      svg {
        @apply w-full h-full;
      }
    }

    .userCurrencyAndId {
      @apply text-texts/70 inline-flex pb-2;

      .userIdDiv {
        @apply text-texts/50 font-medium justify-end text-right self-end text-xs ml-3;
      }
    }

    .walletAmount {
      @apply py-1 inline-block text-3xl text-texts leading-none font-medium lg:text-4xl pr-16 md:pr-24 pb-2;
    }

    .cashbackCountdown {
      @apply text-2xs mt-2 mb-3;
      .countdownTitle {
        @apply bg-error rounded-l py-1 px-1.5 font-semibold;
      }
      .countdownTime {
        @apply bg-white text-black rounded-r py-1 px-2 font-bold;
      }
    }

    .isExpired {
      @apply text-2xs mt-1 mb-3 bg-error rounded-l py-1 px-1.5 font-semibold leading-3;
    }
  }

  .walletBoxBtns {
    @apply flex items-stretch justify-between gap-2 w-full max-w-[400px] mt-auto;

    .walletBtn {
      @apply py-2 px-4 rounded-md flex-1 min-w-28 text-sm md:text-base flex items-center justify-center gap-2;

      &.redeemBtn {
        @apply bg-primary text-button-text font-bold max-w-[50%];

        &[disabled]:not([disabled='false']) {
          @apply opacity-40 cursor-not-allowed;
        }
      }
    }
  }
}

.modalRedeem {
  @apply text-center py-6 px-8 md:py-8 md:px-10;

  .infoText {
    @apply flex flex-col gap-1 bg-texts/5 p-5 rounded-lg;

    span {
      @apply text-texts/50 text-sm;
    }

    strong {
      @apply text-4xl font-extrabold tracking-tight text-primary;
    }
  }

  .confirmText {
    @apply px-2 py-4 text-center flex flex-col gap-1;

    strong {
      @apply text-texts text-xl font-semibold;
    }

    span {
      @apply text-warning text-sm;
    }
  }

  .confirmationButtons {
    @apply grid grid-cols-2 gap-2;

    button {
      @apply py-2 rounded-md capitalize font-semibold;

      &:first-child {
        @apply bg-success text-white;
      }
      &:last-child {
        @apply bg-error text-white;
      }

      &:disabled {
        @apply opacity-40 cursor-not-allowed;
      }
    }
  }
}
</style>
